import { IconX } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { formatSeasonDateTime } from "../bookingSeasonFormat"
import { type FieldIssue, pacificInputToIso, type SeasonField, type SeasonFormState } from "../lib/seasonForm"

/** What every card on the page edits: the shared season draft and the issues to show on it. */
export interface SeasonFieldsProps {
  form: SeasonFormState
  /** Field issues to show — empty until the first save attempt, so a fresh form isn't covered in red. */
  issues: FieldIssue[]
  onChange: (patch: Partial<SeasonFormState>) => void
}

export function issuesFor(issues: FieldIssue[], field: SeasonField): string[] {
  return issues.filter((issue) => issue.field === field).map((issue) => issue.message)
}

export function FieldIssues({ id, issues }: { id: string; issues: string[] }) {
  if (issues.length === 0) return null
  return (
    <div id={id} role="alert" className="flex flex-col gap-0.5 text-sm text-destructive">
      {issues.map((issue) => (
        <span key={issue}>{issue}</span>
      ))}
    </div>
  )
}

interface DateTimeFieldProps {
  id: string
  label: string
  hint: string
  value: string
  issues: string[]
  onChange: (value: string) => void
  /** Optional fields may be left blank, and get a Clear button while they hold a value. */
  optional?: boolean
}

/** A `datetime-local` input read as Pacific wall time, echoing back the instant it means. */
export function DateTimeField({ id, label, hint, value, issues, onChange, optional }: DateTimeFieldProps) {
  const parsed = pacificInputToIso(value)

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>
        {label}
        {optional && <span className="text-muted-foreground font-normal"> (optional)</span>}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="datetime-local"
          value={value}
          aria-invalid={issues.length > 0}
          aria-describedby={`${id}-hint ${id}-issues`}
          onChange={(e) => onChange(e.target.value)}
        />
        {optional && value && (
          <Button type="button" variant="ghost" size="icon" aria-label={`Clear ${label}`} onClick={() => onChange("")}>
            <IconX className="size-4" />
          </Button>
        )}
      </div>
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {parsed.ok ? `${formatSeasonDateTime(parsed.iso)}. ${hint}` : hint}
      </p>
      <FieldIssues id={`${id}-issues`} issues={issues} />
    </div>
  )
}
